/**
 * Hook de histórico de importações — TH-53214 / M2.E7.S2.
 *
 * Lista paginada de batches do tenant. Contrato do backend (TH-53215):
 *   GET /imports?status&store_id&page&per_page  →  { data, meta }
 *
 * Status do batch (worker de import — M2.E3+):
 *   uploaded · parsing · preview · applying · applied · failed · rolled_back
 */

import {
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

export type ImportBatchStatus =
  | "uploaded"
  | "parsing"
  | "preview"
  | "applying"
  | "applied"
  | "failed"
  | "rolled_back";

export interface ImportBatchSummary {
  id: string;
  status: ImportBatchStatus;
  file_url: string;
  store_id: string | null;
  store_name: string | null;
  mapping_id: string | null;
  mapping_name: string | null;
  total_rows: number | null;
  ok_rows: number | null;
  error_rows: number | null;
  warning_rows: number | null;
  created_at: string;
  applied_at: string | null;
  rolled_back_at: string | null;
}

export interface ImportBatchListMeta {
  page: number;
  per_page: number;
  total: number;
}

export interface ImportBatchListResponse {
  data: ImportBatchSummary[];
  meta: ImportBatchListMeta;
}

export interface ImportsHistoryFilters {
  status?: ImportBatchStatus;
  store_id?: string;
  page?: number;
  per_page?: number;
}

function buildQuery(filters: ImportsHistoryFilters): string {
  const sp = new URLSearchParams();
  if (filters.status) sp.set("status", filters.status);
  if (filters.store_id) sp.set("store_id", filters.store_id);
  sp.set("page", String(filters.page ?? 1));
  sp.set("per_page", String(filters.per_page ?? 20));
  return sp.toString();
}

async function fetchImportsHistory(
  filters: ImportsHistoryFilters,
): Promise<ImportBatchListResponse> {
  const res = await fetch(`/api/imports?${buildQuery(filters)}`, {
    credentials: "include",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as ImportBatchListResponse;
}

export function useImportsHistory(
  filters: ImportsHistoryFilters,
): UseQueryResult<ImportBatchListResponse, Error> {
  return useQuery({
    queryKey: ["imports-history", filters],
    queryFn: () => fetchImportsHistory(filters),
  });
}
